import { BigNum } from '@velocity-exchange/sdk';
import { camelCase } from 'lodash';
import React, { useCallback } from 'react';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import {
	capStringFractionDigits,
	formatText,
} from '@velocity-exchange/common/format';
import { HIDDEN_ACCOUNT_VALUE_TEXT } from 'src/components/NumberDisplay/NumberDisplay';

const HEADER_LABELS = ['Price', 'Order size', 'Percentage'];

export type Order = {
	price: BigNum;
	size: BigNum;
	percentage: BigNum;
	symbol: string;
};

interface Props {
	orders: Order[];
	tickSizeDecimalPlaces: number;
	stepSizeDecimalPlaces: number;
}

export default function ScaledOrdersTable({
	orders,
	tickSizeDecimalPlaces,
	stepSizeDecimalPlaces,
}: Props) {
	const getDisplayValue = useGetDisplayValue();

	if (!orders.length) {
		return null;
	}

	return (
		<div className="rounded-sm border border-stroke-divider typo-body-xs">
			<div className="flex w-full border-b border-stroke-divider px-md py-sm text-text-secondary">
				{HEADER_LABELS.map((label) => (
					<span
						key={`header_${camelCase(label)}`}
						className="basis-1/3 last:text-right"
					>
						{label}
					</span>
				))}
			</div>
			<div className="flex thin-scroll max-h-32 flex-col gap-y-sm overflow-y-auto px-md py-sm text-text-default sm:max-h-60">
				{orders.map(({ price, size, percentage, symbol }, index) => {
					return (
						<div className="flex" key={`order_${price}_${size}_${index}`}>
							<span className="basis-1/3">
								${getDisplayValue(price, tickSizeDecimalPlaces)}
							</span>
							<span className="basis-1/3">
								{getDisplayValue(size, stepSizeDecimalPlaces, true)} {symbol}
							</span>
							<span className="basis-1/3 text-right">
								{getDisplayValue(percentage, 2)}%
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

/**
 * Either returns the value formatted or a placeholder if account values are hidden.
 */
function useGetDisplayValue() {
	const showAccountValues = useShowAccountValues();

	return useCallback(
		(value: BigNum, decimalPlaces: number, isSize?: boolean) => {
			if (!showAccountValues) {
				return HIDDEN_ACCOUNT_VALUE_TEXT;
			}

			if (isSize) {
				return capStringFractionDigits(value.toNum().toString(), {
					maxFractionDigits: decimalPlaces,
				});
			}

			return formatText(value.toNum(), {
				digits: {
					kind: 'decimals',
					decimals: decimalPlaces,
					rounding: 'half-ceil',
				},
			});
		},
		[showAccountValues]
	);
}
